import type { ReactNode } from "react";
import { cn } from "@/utils/cn";
import { WizardActionsBar } from "./WizardActionsBar";

export interface PageContainerProps {
  /** Page content (scrollable). */
  children: ReactNode;
  /** Optional buttons for the pinned action bar (e.g. Back, Continue, Submit). Omit for an empty bar. */
  actions?: ReactNode;
  /** Extra class for the outer container. */
  className?: string;
}

/**
 * Same main container as dashboard cards. Optional actions render the complaint wizard
 * Back/Next row below the scroll area (layout flow, not position:fixed).
 * Below sm the card is flattened (no background, border, shadow, rounding or padding) so
 * content sits on the page background at the layout's 16px gutter; overflow stays visible
 * there so focus rings at the edge aren't clipped (the layout's main clips horizontal
 * overflow instead).
 */
export function PageContainer({
  children,
  actions,
  className,
}: PageContainerProps) {
  return (
    <div
      className={cn(
        "flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col bg-transparent sm:overflow-hidden sm:bg-white sm:rounded-lg sm:border sm:border-slate-200 sm:shadow-[0_2px_12px_rgba(0,0,0,0.08)]",
        className
      )}
    >
      <div
        data-page-container-scroll
        className="flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col p-0 sm:overflow-y-auto sm:overflow-x-hidden sm:p-4"
      >
        {children}
      </div>
      {actions && <WizardActionsBar>{actions}</WizardActionsBar>}
    </div>
  );
}
